import { useAddress } from '@/store/selector';
import { TradeData } from '@/types/trade';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFees } from './useGetFees';
import { formatDisplay } from '@/utils/format-display';
dayjs.extend(utc);
function CountDown({
    pairIndex,
    index,
    createdAt,
    buy,
    initialPosToken,
    leverage,
}: {
    pairIndex: number;
    index: number;
    createdAt: number | string;
    buy: boolean;
    initialPosToken: string;
    leverage: number;
}) {
    const { t } = useTranslation();

    const address = useAddress();
    const { data, mutate, isLoading, isValidating } = useGetFees(
        address,
        pairIndex,
        index,
        createdAt,
        buy,
        initialPosToken,
        leverage
    );

    const targetTime = useMemo(() => {
        if (!data?.updatedAt) return 0;
        return dayjs.utc(data.updatedAt).add(4, 'hour').valueOf();
    }, [data?.updatedAt]);
    const [timeLeft, setTimeLeft] = useState(() =>
        Math.max(targetTime - dayjs.utc().valueOf(), 0)
    );

    // Tính thời gian còn lại (4h kể từ updatedAt)

    // Cập nhật mỗi giây
    useEffect(() => {
        if (!targetTime) return;

        const interval = setInterval(() => {
            const diff = targetTime - dayjs.utc().valueOf();
            setTimeLeft(diff > 0 ? diff : 0);
        }, targetTime);

        return () => clearInterval(interval);
    }, [targetTime]);

    // Convert ms → hh:mm:ss
    const formatTime = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
            2,
            '0'
        );
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const handleMutateEstimate = async () => {
        await mutate();
    };

    return (
        <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-base text-slate-900 ">{t('Fees')}</h3>
            <div className="flex items-center gap-3">
                <span className="text-xs text-[#6F767E]  tabular-nums tracking-wide">
                    {formatTime(timeLeft)}
                </span>
                <button
                    onClick={handleMutateEstimate}
                    disabled={timeLeft > 0}
                    className="text-xs bg-slate-200  text-slate-600  px-3 py-1 rounded-lg hover:bg-slate-300  transition-colors disabled:opacity-60"
                >
                    {isValidating || isLoading ? (
                        <div
                            className="animate-spin inline-block size-4 border-3 border-[#333333] border-t-white text-white rounded-full dark:text-blue-500"
                            role="status"
                            aria-label="loading"
                        />
                    ) : (
                        t('Estimate')
                    )}
                </button>
            </div>
        </div>
    );
}

const CountDownView = memo(CountDown);

export default function FeesCard({ item }: { item: TradeData }) {
    const { t } = useTranslation();

    const address = useAddress();

    const { data } = useGetFees(
        address,
        item.indexPrice,
        item.index,
        item.createdAt,
        item.buy,
        item.initialPosToken,
        item.leverage
    );

    return (
        <div className="bg-slate-50 rounded-xl p-4 mb-4">
            <CountDownView
                pairIndex={item.indexPrice}
                index={item.index}
                createdAt={item.createdAt}
                buy={item.buy}
                initialPosToken={item.initialPosToken}
                leverage={item.leverage}
            />
            <div className="border-t border-dashed border-slate-200  mb-3" />
            <div className="space-y-1.5 mb-3">
                <div className="flex justify-between text-xs">
                    <span className="text-[#6F767E] ">{t('Open fee')}</span>
                    <span className="font-medium text-slate-900 ">
                        {item.openFeeValue} WLD
                    </span>
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-[#6F767E] ">{t('Funding fee')}</span>
                    <span className="font-medium text-slate-900 ">
                        {formatDisplay(Number(data?.fundingFee) || 0, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </span>
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-[#6F767E] ">{t('Rollover fee')}</span>
                    <span className="font-medium text-slate-900 ">
                        {formatDisplay(Number(data?.rolloverFee) || 0, {
                            decimalToShow: 2,
                        })}{' '}
                        WLD
                    </span>
                </div>
            </div>
            {data?.updatedAt && (
                <div className="border-t border-dashed border-slate-200  pt-3 flex justify-center">
                    <span className="text-[10px] text-slate-400  font-medium">
                        {t('Last updated at')}:{' '}
                        {dayjs
                            .utc(data.updatedAt)
                            .format('DD/MM/YYYY - HH:mm:ss')}{' '}
                    </span>
                </div>
            )}
        </div>
    );
}
